import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Moon, Save } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { updatePreferences } from "@/lib/api";

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const QuietHoursDialog = ({ preferences, onClose, onSaved }) => {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(!!preferences?.quiet_enabled);
  const [start, setStart] = useState(preferences?.quiet_start || "22:00");
  const [end, setEnd] = useState(preferences?.quiet_end || "07:00");
  const [saving, setSaving] = useState(false);

  // Sync when the prop updates (first fetch)
  useEffect(() => {
    setEnabled(!!preferences?.quiet_enabled);
    setStart(preferences?.quiet_start || "22:00");
    setEnd(preferences?.quiet_end || "07:00");
  }, [preferences]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!HHMM.test(start) || !HHMM.test(end)) {
      toast.error("INVALID TIME");
      return;
    }
    try {
      setSaving(true);
      const next = await updatePreferences({
        quiet_enabled: enabled,
        quiet_start: start,
        quiet_end: end,
      });
      toast.success(t("quiet.saved"));
      onSaved?.(next);
      onClose?.();
    } catch (err) {
      toast.error(t("quiet.failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="quiet-hours-dialog"
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/60"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSave}
        className="relative bg-white brutal-border brutal-shadow w-full max-w-md p-6 font-mono"
      >
        <button
          type="button"
          data-testid="quiet-hours-close"
          onClick={onClose}
          className="absolute top-3 right-3 bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#0014C7] text-white brutal-border w-10 h-10 flex items-center justify-center">
            <Moon className="w-5 h-5" strokeWidth={3} />
          </div>
          <div>
            <h3 className="font-display text-xl uppercase leading-none">
              {t("quiet.title")}
            </h3>
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
              {t("quiet.badge")}
            </div>
          </div>
        </div>

        <p className="font-mono text-xs text-neutral-700 mb-4 leading-relaxed">
          {t("quiet.hint")}
        </p>

        <button
          type="button"
          data-testid="quiet-toggle"
          onClick={() => setEnabled((v) => !v)}
          className={`w-full flex items-center justify-between gap-3 brutal-border p-3 brutal-shadow-sm brutal-interactive mb-4 ${
            enabled ? "bg-[#E5FF00]" : "bg-white"
          }`}
        >
          <span className="font-display uppercase text-sm">
            {enabled ? t("quiet.toggleOn") : t("quiet.toggleOff")}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest">
            {enabled ? "[ON]" : "[OFF]"}
          </span>
        </button>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest mb-1 font-bold text-neutral-600">
              {t("quiet.start")}
            </label>
            <input
              data-testid="quiet-start"
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              disabled={!enabled}
              className="w-full bg-white brutal-border p-3 text-base font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors disabled:opacity-40"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest mb-1 font-bold text-neutral-600">
              {t("quiet.end")}
            </label>
            <input
              data-testid="quiet-end"
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              disabled={!enabled}
              className="w-full bg-white brutal-border p-3 text-base font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors disabled:opacity-40"
            />
          </div>
        </div>
        <p className="font-mono text-[10px] text-neutral-500 mb-4 italic">
          {t("quiet.wrapNote")}
        </p>

        <button
          type="submit"
          data-testid="quiet-save"
          disabled={saving}
          className="w-full bg-black text-white brutal-border px-4 py-3 font-display uppercase text-base brutal-shadow brutal-interactive disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" strokeWidth={3} />
          {saving ? t("quiet.saving") : t("quiet.save")}
        </button>
      </form>
    </div>
  );
};

export default QuietHoursDialog;
